import { Grid, Paper, Stack } from '@mui/material'
import {
    CarrierType,
    CustomsResponseDto,
    DangerousGoodsDefinitionDto,
    DoorDirection,
    UnitLabelDto,
    UnitLabelType,
    UnloadingMethod,
    WeightClassResponseDto,
} from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { DirectionalCargoDetailsType } from '@planning/pages/Order/V2/organisms/DirectionalCargoDetailsSubForm'
import { CargoDetailsSubFormStore } from '@planning/pages/Order/V2/stores/CargoDetailsSubFormStore'
import { OrderFormStore, OrderFormStoreType } from '@planning/pages/Order/V2/stores/OrderFormStore'
import { StowageInstructionSubFormType } from '@planning/pages/Order/V2/stores/StowageInstructionsSubFormStore'
import { StowageInstructionAutocompleteViewStore } from '@planning/stores/stowageInstruction/StowageInstructionAutocompleteViewStore'
import { useTranslate } from '@tolgee/react'
import { DataControlCard, SectionedCardWithAttributes, SimpleCardWithAttributes } from '@tom-ui/ui'
import { IFormStore } from '@tom-ui/utils'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useMemo } from 'react'
import { OrderWeightClass } from '../molecules/OrderWeightClass'
import { OrderViewCardProps } from '../steps/OrderReviewStep'
import { OrderAditionalCargoInformationCard } from './OrderAditionalCargoInformationCard'
import { OrderCargoInformationCard } from './OrderCargoInformationCard'
import { OrderSpecialHandlingCard } from './OrderSpecialHandlingCard'

export type OrderCargoViewCardAttributes = {
  hasSeals?: boolean | null
  customs?: CustomsResponseDto[] | null | undefined
  isEmpty?: boolean | null
  grossWeight?: string | null
  vgm?: string | null
  weightClass?: WeightClassResponseDto | null
  content?: string | null
  orderReference?: string | null
  customerName?: string | null
  consignee?: string | null
  dangerousGoods?: boolean
  oog?: boolean | null
  oogDimensions?: string | null
  unloadingMethod?: UnloadingMethod | null
  imoClasses?: string[] | null
  unNumbers?: DangerousGoodsDefinitionDto[] | null
  otherLabels?: UnitLabelType[] | null
  doorDirection?: DoorDirection | null
  unitLabel?: UnitLabelDto | null
  stowageInstruction?: StowageInstructionSubFormType | null
  hideStowageInstruction?: boolean

  billOfLading?: string | null
  shippingOrderReference?: string | null
  releaseReference?: string | null

  origin?: string | null
  inlandDestination?: string | null
  finalDestination?: string | null
  portOfLoading?: string | null
  portOfDischarge?: string | null

  isApprovedInbound?: boolean | null
  isApprovedOutbound?: boolean | null
  isOrderFulfilled?: boolean | null

  shipperId?: number | null
  forwarderId?: number | null
}

const CargoViewMode = observer(
  (props: OrderViewCardProps<OrderCargoViewCardAttributes>) => {
    const { attributes } = props

    const {
      hasSeals,
      customs,
      isEmpty,
      grossWeight,
      vgm,
      weightClass,
      content,
      orderReference,
      customerName,
      consignee,
      dangerousGoods,
      unitLabel,
      oog,
      oogDimensions,
      unloadingMethod,
      doorDirection,
      otherLabels,
      imoClasses,
      stowageInstruction,
      hideStowageInstruction = false,
      billOfLading,
      shippingOrderReference,
      origin,
      inlandDestination,
      finalDestination,
      portOfLoading,
      portOfDischarge,
      forwarderId,
      shipperId,
    } = attributes

    const { t } = useTranslate()
    const { companyItemStore } = usePlanningStore()

    const hasUnitLabels = computed(() => {
      return (
        unitLabel?.imoClasses?.length || unitLabel?.unNumbers?.length || unitLabel?.types?.length
      )
    }).get()

    const specialHandlingAttributes = () => {
      const attributes = []

      if (!dangerousGoods) {
        attributes.push({
          label: t('dangerousGoods', 'Dangerous goods'),
          value: t('no', 'No'),
        })
      } else {
        attributes.push(
          {
            label: t('imoClass', 'IMO Class'),
            value: imoClasses?.join(', ') || '-',
          },
          {
            label: t('otherLabels', 'Other labels'),
            value: otherLabels?.map(type => t(type)).join(', ') || '-',
          },
        )
      }

      if (!oog) {
        attributes.push({
          label: t('oog', 'OOG'),
          value: t('no', 'No'),
        })
      } else {
        attributes.push({
          label: t('oogTLRFR', 'OOG (TxLxRxFxR)'),
          value: oogDimensions,
        })
        attributes.push({
          label: t('unloadingMethod', 'Unloading method'),
          value: unloadingMethod,
        })
      }

      if (doorDirection) {
        attributes.push({
          label: t('doorDirection', 'Door direction'),
          value: t(doorDirection),
        })
      }
      if (!hideStowageInstruction) {
        if (stowageInstruction?.hasStowageInstruction) {
          attributes.push(
            {
              label: t('stowageInstruction', 'Stowage instruction'),
              value: stowageInstruction.name,
            },
            {
              label: t('stowageDescription', 'Stowage description'),
              value: stowageInstruction.description,
            },
          )
        } else {
          attributes.push({
            label: t('stowageInstruction', 'Stowage instruction'),
            value: t('no', 'No'),
          })
        }
      }

      return attributes
    }

    const forwarder = forwarderId ? companyItemStore.elements[forwarderId]?.data : undefined
    const shipper = shipperId ? companyItemStore.elements[shipperId]?.data : undefined

    useEffect(() => {
      if (forwarderId && !forwarder) companyItemStore.fetchById(forwarderId)
      if (shipperId && !shipper) companyItemStore.fetchById(shipperId)
    }, [forwarderId, shipperId, forwarder, shipper, companyItemStore])

    const docsAttrs: { label: string; value: React.ReactNode; sizeXs?: number }[] = []
    if (orderReference?.trim())
      docsAttrs.push({ label: t('bookingNo', 'Booking no.'), value: orderReference, sizeXs: 6 })

    if (billOfLading?.trim())
      docsAttrs.push({ label: t('blNumber', 'B/L no.'), value: billOfLading, sizeXs: 6 })

    if (shippingOrderReference?.trim())
      docsAttrs.push({
        label: t('shippingOrderReference', 'Shipping order reference'),
        value: shippingOrderReference,
        sizeXs: 6,
      })

    const stakeholderAttrs: { label: string; value: React.ReactNode; sizeXs?: number }[] = []
    if (customerName?.trim())
      stakeholderAttrs.push({
        label: t('customerName', 'Customer name'),
        value: customerName,
        sizeXs: 6,
      })
    if (consignee?.trim())
      stakeholderAttrs.push({ label: t('consignee', 'Consignee'), value: consignee, sizeXs: 6 })
    if (forwarder?.name)
      stakeholderAttrs.push({
        label: t('forwarder', 'Forwarder'),
        value: forwarder.name,
        sizeXs: 6,
      })
    if (shipper?.name)
      stakeholderAttrs.push({ label: t('shipper', 'Shipper'), value: shipper.name, sizeXs: 6 })

    const locationAttrs: { label: string; value: React.ReactNode; sizeXs?: number }[] = []
    if (origin?.trim())
      locationAttrs.push({ label: t('origin', 'Origin'), value: origin, sizeXs: 3 })
    if (inlandDestination?.trim())
      locationAttrs.push({
        label: t('inlandDestination', 'Inland destination'),
        value: inlandDestination,
        sizeXs: 3,
      })
    if (finalDestination?.trim())
      locationAttrs.push({
        label: t('finalDestination', 'Final destination'),
        value: finalDestination,
        sizeXs: 3,
      })
    if (portOfLoading?.trim())
      locationAttrs.push({
        label: t('portOfLoading', 'Port of loading'),
        value: portOfLoading,
        sizeXs: 3,
      })
    if (portOfDischarge?.trim())
      locationAttrs.push({
        label: t('portOfDischarge', 'Port of discharge'),
        value: portOfDischarge,
        sizeXs: 3,
      })

    return (
      <Grid container spacing={2}>
        <Grid
          size={{
            sm: 12,
            md: 6,
          }}
        >
          <SectionedCardWithAttributes
            title={t('cargo', 'Cargo')}
            showDividers
            sections={[
              { subtitle: '', attributes: docsAttrs },
              { subtitle: t('stakeholder', 'Stakeholder'), attributes: stakeholderAttrs },
              { subtitle: t('locations', 'Locations'), attributes: locationAttrs },
            ]}
          />
        </Grid>
        <Grid
          size={{
            sm: 12,
            md: 6,
          }}
        >
          <SimpleCardWithAttributes
            title={t('cargoDetails', 'Cargo details')}
            attributes={[
              {
                label: t('status', 'Status'),
                value: isEmpty ? t('empty', 'Empty') : t('full', 'Full'),
              },
              {
                label: t('grossWeight', 'Gross weight'),
                value: grossWeight || '-',
              },
              {
                label: t('vgm', 'VGM'),
                value: vgm || '-',
              },
              {
                label: '',
                value: <OrderWeightClass weightClassDto={weightClass} />,
                sizeXs: 12,
              },
              {
                label: t('content', 'Content'),
                value: content || '-',
                sizeXs: 12,
              },
            ]}
          />
        </Grid>
        <Grid
          size={{
            sm: 12,
            md: 6,
          }}
        >
          <SimpleCardWithAttributes
            title={t('seals', 'Seals')}
            attributes={[
              {
                label: t('seals', 'Seals'),
                value: hasSeals ? t('yes', 'Yes') : t('no', 'No'),
              },
            ]}
          />
        </Grid>

        {!!customs?.length && (
          <Grid
            size={{
              sm: 12,
              md: 6,
            }}
          >
            <SimpleCardWithAttributes
              title={t('customReferencesAndStatus', 'Customs references & status')}
              attributes={[
                {
                  label: t('customReference', 'Customs reference'),
                  value: (
                    <>
                      {customs.map(c => (
                        <Stack key={c.reference}>{c.reference}</Stack>
                      ))}
                    </>
                  ),
                },
                {
                  label: t('status', 'Status'),
                  value: (
                    <>
                      {customs.map(c => (
                        <Stack key={c.reference}>{c.status}</Stack>
                      ))}
                    </>
                  ),
                },
              ]}
            />
          </Grid>
        )}
        <Grid
          size={{
            sm: 12,
            md: hasUnitLabels ? 6 : 12,
          }}
        >
          <SimpleCardWithAttributes
            title={t('specialHandling', 'Special handling')}
            attributes={specialHandlingAttributes()}
          />
        </Grid>
        <Grid
          size={{
            sm: 12,
            md: 6,
          }}
        >
          {hasUnitLabels && (
            <SimpleCardWithAttributes
              title={t('observedLabels', 'Observed labels')}
              attributes={[
                {
                  label: t('imoClass', 'IMO Class'),
                  value: unitLabel?.imoClasses?.join(', '),
                },
                {
                  label: t('unNumbers', 'UN Numbers'),
                  value: unitLabel?.unNumbers?.join(', '),
                },
                {
                  label: t('otherLabels', 'Other labels'),
                  value: unitLabel?.types?.map(type => t(type)).join(', '),
                },
              ]}
            />
          )}
        </Grid>
      </Grid>
    )
  },
)

const CargoEditMode = observer(
  (props: {
    formStore: OrderFormStore
    cargoDetailsFormStore: CargoDetailsSubFormStore
    directionalCargoDetailsFormStore: IFormStore<DirectionalCargoDetailsType>
    initialValues?: Partial<OrderFormStoreType>
    directionalInitialValues?: Partial<DirectionalCargoDetailsType>
    isSmallView?: boolean
    hasCustoms?: boolean
    hasDoorDirection?: boolean
    hideStowageInstruction?: boolean
    disableUnloadingMethod?: boolean
  }) => {
    const {
      formStore,
      cargoDetailsFormStore,
      directionalCargoDetailsFormStore,
      initialValues,
      directionalInitialValues,
      isSmallView,
      hasCustoms,
      hasDoorDirection,
      hideStowageInstruction = false,
      disableUnloadingMethod,
    } = props

    const stowageInstructionsAutocompleteViewStore = useMemo(
      () => new StowageInstructionAutocompleteViewStore(),
      [],
    )

    return (
      <Stack spacing={2} width={'100%'}>
        <OrderCargoInformationCard
          formStore={formStore}
          cargoDetailsFormStore={cargoDetailsFormStore}
          initialValues={initialValues}
        />

        <Grid size={12}>
          <OrderAditionalCargoInformationCard
            isSmallView={isSmallView}
            cargoDetailsFormStore={directionalCargoDetailsFormStore}
            initialValues={directionalInitialValues}
            hasCustoms={hasCustoms}
            hasDoorDirection={hasDoorDirection}
          />
        </Grid>

        <Paper variant='outlined' sx={{ p: '1rem' }}>
          <Grid container spacing={2}>
            <Grid
              size={{
                md: 12,
              }}
            >
              <OrderSpecialHandlingCard
                directionalCargoDetailsFormStore={directionalCargoDetailsFormStore}
                cargoDetailsFormStore={cargoDetailsFormStore}
                initialValues={directionalInitialValues}
                autoCompleteStore={stowageInstructionsAutocompleteViewStore}
                hideStowageInstruction={hideStowageInstruction}
                disableUnloadingMethod={disableUnloadingMethod}
              />
            </Grid>
          </Grid>
        </Paper>
      </Stack>
    )
  },
)

export const OrderCargoViewCard = observer(
  (
    props: OrderViewCardProps<OrderCargoViewCardAttributes> & {
      title: string
      isSmallView?: boolean
      formStore?: OrderFormStore
      cargoDetailsFormStore?: CargoDetailsSubFormStore
      directionalCargoDetailsFormStore?: IFormStore<DirectionalCargoDetailsType>
      initialValues?: Partial<OrderFormStoreType>
      directionalInitialValues?: Partial<DirectionalCargoDetailsType>
      hideCustoms?: boolean
      hideDoorDirection?: boolean
      cargoFormIntoEditMode?: boolean
    },
  ) => {
    const {
      title,
      attributes,
      formStore,
      cargoDetailsFormStore,
      directionalCargoDetailsFormStore,
      initialValues,
      directionalInitialValues,
      hideActions,
      allowEdit,
      isSmallView,
      hideCustoms,
      hideDoorDirection,
      cargoFormIntoEditMode,
      onClickSave,
      onClickEdit,
    } = props

    const { hideStowageInstruction, isOrderFulfilled } = attributes

    const hasCustoms = computed(() => {
      const hasCustoms =
        !hideCustoms &&
        initialValues?.outboundCargoDetails?.transport?.carrierType === CarrierType.Vessel
      return hasCustoms
    }).get()

    const hasDoorDirection = computed(() => {
      const hasDoorDirection =
        !hideDoorDirection &&
        initialValues?.outboundCargoDetails?.transport?.carrierType === CarrierType.Train
      return hasDoorDirection
    }).get()

    const handleClickSave = useCallback(async () => {
      await onClickSave?.()
    }, [onClickSave])

    return (
      <DataControlCard
        dataCy='order-cargo-view-card'
        title={title}
        hideActions={hideActions}
        allowEdit={allowEdit}
        onClickEdit={onClickEdit}
        onClickSave={handleClickSave}
        defaultEditMode={cargoFormIntoEditMode}
        viewComponent={<CargoViewMode attributes={attributes} />}
        editComponent={
          cargoDetailsFormStore &&
          directionalCargoDetailsFormStore &&
          formStore && (
            <CargoEditMode
              isSmallView={isSmallView}
              cargoDetailsFormStore={cargoDetailsFormStore}
              directionalCargoDetailsFormStore={directionalCargoDetailsFormStore}
              formStore={formStore}
              initialValues={initialValues}
              directionalInitialValues={directionalInitialValues}
              hasCustoms={hasCustoms}
              hasDoorDirection={hasDoorDirection}
              hideStowageInstruction={hideStowageInstruction}
              disableUnloadingMethod={!isOrderFulfilled}
            />
          )
        }
      />
    )
  },
)
